/**
 * CanvasToolsPanel — a compact floating toolbar (draggable, like the Add menu)
 * for canvas-level controls that don't belong in the content palette:
 *   • zoom: a % readout (click → reset to 100%) with − / + buttons (pinch still
 *     works; these are the discoverable equivalent).
 *   • history: undo / redo, wired to the site's Y.UndoManager (same stack the
 *     ⌘Z / ⌘⇧Z shortcuts drive), disabled when their stack is empty.
 *
 * Lives inside the ReactFlow + Editor providers (InnerCanvas) so it can read the
 * live zoom and the undo manager. position:fixed so it stays put while you pan.
 */
import { useEffect, useState } from "react";
import { html, css } from "react-strict-dom";
import { Tooltip } from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { microSpacing, radii } from "@duro-app/tokens/tokens/spacing.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";
import { useReactFlow, useViewport } from "./xyflow.js";
import { useEditorContext } from "./EditorContext.js";
import { useDraggablePanel } from "./useDraggablePanel.js";
import { EditorButton } from "./ui/EditorButton.js";

/** A toolbar button: its title is both the tooltip and the accessible name,
 *  and its press never starts a panel drag. */
function ToolButton({
  onClick,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <EditorButton
      title={title}
      aria-label={title}
      disabled={disabled}
      isolatePress
      onClick={onClick}
    >
      {children}
    </EditorButton>
  );
}

export function CanvasToolsPanel() {
  const { zoom } = useViewport();
  const { zoomIn, zoomOut, zoomTo } = useReactFlow();
  const { undoManager } = useEditorContext();
  const initial =
    typeof window !== "undefined"
      ? {
          x: Math.max(16, window.innerWidth / 2 - 110),
          y: window.innerHeight - 62,
        }
      : { x: 16, y: 16 };
  const { ref, pos, onHandleDown } = useDraggablePanel(initial);

  const [{ canUndo, canRedo }, setHist] = useState({
    canUndo: false,
    canRedo: false,
  });
  useEffect(() => {
    const update = () =>
      setHist({
        canUndo: undoManager.undoStack.length > 0,
        canRedo: undoManager.redoStack.length > 0,
      });
    update();
    const events = [
      "stack-item-added",
      "stack-item-popped",
      "stack-cleared",
      "stack-item-updated",
    ] as const;
    events.forEach((e) => undoManager.on(e, update));
    return () => events.forEach((e) => undoManager.off(e, update));
  }, [undoManager]);

  return (
    <html.div ref={ref} style={[s.panel, s.at(pos.x, pos.y)]}>
      {/* Drag handle */}
      <Tooltip.Root content="Drag to move">
        <Tooltip.Trigger>
          <html.div onPointerDown={onHandleDown} style={s.handle}>
            ⠿
          </html.div>
        </Tooltip.Trigger>
      </Tooltip.Root>

      <ToolButton onClick={() => zoomOut()} title="Zoom out">
        −
      </ToolButton>
      <ToolButton
        onClick={() => zoomTo(1, { duration: 150 })}
        title="Reset zoom to 100%"
      >
        <html.span style={s.readout}>{Math.round(zoom * 100)}%</html.span>
      </ToolButton>
      <ToolButton onClick={() => zoomIn()} title="Zoom in">
        +
      </ToolButton>

      <html.div style={s.divider} />

      <ToolButton
        onClick={() => undoManager.undo()}
        disabled={!canUndo}
        title="Undo (⌘Z)"
      >
        ↶
      </ToolButton>
      <ToolButton
        onClick={() => undoManager.redo()}
        disabled={!canRedo}
        title="Redo (⌘⇧Z)"
      >
        ↷
      </ToolButton>
    </html.div>
  );
}

const s = css.create({
  panel: {
    position: "fixed",
    zIndex: layers.overlay,
    display: "flex",
    alignItems: "center",
    gap: microSpacing.px2,
    paddingTop: microSpacing.px3,
    paddingBottom: microSpacing.px3,
    paddingLeft: microSpacing.px5,
    paddingRight: microSpacing.px5,
    backgroundColor: colors.bgCard,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.md,
    boxShadow: shadows.md,
  },
  at: (left: number, top: number) => ({ left, top }),
  handle: {
    display: "flex",
    alignItems: "center",
    height: sizes.controlSm,
    paddingLeft: microSpacing.px2,
    paddingRight: microSpacing.px2,
    cursor: "move",
    color: colors.border,
    touchAction: "none",
    userSelect: "none",
    fontSize: typography.fontSizeXs,
    letterSpacing: "0.1em",
  },
  readout: {
    fontWeight: typography.fontWeightSemibold,
    fontVariantNumeric: "tabular-nums",
  },
  divider: {
    width: sizes.divider,
    height: sizes.iconMd,
    backgroundColor: colors.border,
    marginLeft: microSpacing.px3,
    marginRight: microSpacing.px3,
  },
});
